import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { animated } from '@react-spring/web';
import type { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ConfirmSlider } from '@sorare/core/src/atoms/ui/ConfirmSlider';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { EssenceFlavours } from '#src/components/essence/CardPullExperience/CardPullInfos/EssenceFlavours/index.tsx';
import type { CardRevealUIState } from '#src/components/essence/CardPullExperience/CardsReveal/types.ts';

import { CraftCluesBalance } from './CraftCluesBalance/index.tsx';
import { EssenceRarityPills } from './EssenceRarityPills/index.tsx';
import { RaritySwitcher } from './RaritySwitcher/index.tsx';
import type {
  CardsPull_cardPullConfig,
  CardsPull_cardShardsChest,
} from './__generated__/index.graphql.ts';
import cardPullBg from './assets/cardPullExperience.webp';
import { useUIStyles } from './useUIStyles.ts';

const Wrapper = styled(Vertical)`
  flex-grow: 1;
  position: relative;
  @media ${laptopAndAbove} {
    padding: var(--quadruple-unit);
    background:
      linear-gradient(to bottom, var(--c-nd-50) 0%, var(--c-nd-50) 100%),
      no-repeat center/cover var(--card-pull-background);
  }
`;

const RaritySwitcherWrapper = styled(Vertical)`
  flex: 1;
  align-self: flex-end;
`;

const BezelContent = styled(Vertical)`
  flex-grow: 1;
  justify-content: flex-end;
  width: 100%;
  gap: var(--triple-unit);
  @media ${laptopAndAbove} {
    flex-grow: unset;
    padding: var(--double-unit) var(--double-unit) 0;
    height: calc(
      var(--100vh) - var(--current-stack-height) - calc(8 * var(--unit))
    );
  }
`;

const CtaWrapper = styled(Horizontal)`
  flex: 1;
  position: relative;
`;

const MobileCluesWrapper = styled(Horizontal)`
  align-self: center;
`;
const CluesWrapper = styled(Horizontal)`
  flex-grow: 1;
  align-items: flex-end;
`;

const ActionsBar = styled.div`
  position: relative;
  padding-bottom: var(--unit);
  @media ${laptopAndAbove} {
    padding: var(--double-unit);
    padding: unset;
    display: grid;
    grid-template-columns: 1fr min-content 1fr;
    justify-content: center;
  }
`;

const BalanceChangeWrapper = styled.div`
  position: absolute;
  gap: var(--half-unit);
  top: 0;
  left: 0;
  right: 0;
`;

type Props = {
  cardPullConfigs: CardsPull_cardPullConfig[];
  cardShardsChest: CardsPull_cardShardsChest;
  onConfirm: () => Promise<void>;
  uiState: CardRevealUIState;
  balanceChange: ReactNode;
  children: ReactNode;
};

export const CardsPull = ({
  cardPullConfigs,
  cardShardsChest,
  onConfirm,
  uiState,
  balanceChange,
  children,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const {
    flags: { useEssenceCompetitionStrip = false },
  } = useFeatureFlags();
  const { rarity } = cardShardsChest;
  const uiStyles = useUIStyles(uiState);
  if (rarity === Rarity.common || rarity === Rarity.custom_series) {
    return null;
  }

  const threshold = cardShardsChest?.thresholds.at(0)?.threshold;
  const sliderColor = getRarityColor(rarity);
  return (
    <animated.div
      style={{
        ...uiStyles,
        pointerEvents: uiState === 'idle' ? 'auto' : 'none',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
      }}
    >
      <Wrapper gap={2}>
        <ConditionalWrapper
          wrap={isLaptopAndAbove}
          Wrapper={Bezel}
          props={{
            background: `no-repeat center/cover var(--card-pull-background)`,
          }}
        >
          <BezelContent>
            {children}
            {!['pulled', 'selected', 'pickRevealed', 'allRevealed'].includes(
              uiState
            ) && (
              <ConditionalWrapper
                wrap={!isLaptopAndAbove}
                Wrapper={BottomPortal}
              >
                <animated.div
                  style={
                    isLaptopAndAbove
                      ? undefined
                      : {
                          ...uiStyles,
                          pointerEvents: uiState === 'idle' ? 'auto' : 'none',
                        }
                  }
                >
                  <Vertical gap={2}>
                    {!isLaptopAndAbove && !useEssenceCompetitionStrip && (
                      <MobileCluesWrapper>
                        <CraftCluesBalance cardPullConfigs={cardPullConfigs} />
                      </MobileCluesWrapper>
                    )}
                    {!!useEssenceCompetitionStrip && (
                      <Horizontal center>
                        <EssenceFlavours
                          id={cardShardsChest.id}
                          rarity={rarity}
                          cardPull={null}
                          fullWidth={false}
                          pill
                        />
                      </Horizontal>
                    )}
                    <ActionsBar>
                      {!!isLaptopAndAbove && (
                        <RaritySwitcherWrapper center>
                          <RaritySwitcher
                            rarity={rarity}
                            shardsChest={cardShardsChest}
                          />
                        </RaritySwitcherWrapper>
                      )}
                      <CtaWrapper center>
                        <BalanceChangeWrapper>
                          {balanceChange}
                        </BalanceChangeWrapper>
                        <ConfirmSlider
                          onConfirm={onConfirm}
                          color={sliderColor}
                          disabled={
                            cardShardsChest.cardShardsCount <
                            (cardShardsChest.thresholds.at(0)?.threshold || 0)
                          }
                          ctaMessage={
                            <>
                              <FormattedMessage
                                defaultMessage="Draw {rarity}"
                                values={{
                                  rarity: scarcityNames[rarity],
                                }}
                              />{' '}
                              <Dot size={0.5} /> <Shards />x
                              {!!threshold && (
                                <FormattedNumber
                                  value={threshold}
                                  useGrouping
                                />
                              )}
                            </>
                          }
                          style={
                            {
                              '--background-image': `url(${cardPullBg})`,
                              '--background-color': `var(--card-pull-color)`,
                            } as React.CSSProperties
                          }
                        />
                      </CtaWrapper>
                      {!!isLaptopAndAbove && (
                        <Vertical center gap={0}>
                          <CluesWrapper>
                            <CraftCluesBalance
                              cardPullConfigs={cardPullConfigs}
                            />
                          </CluesWrapper>
                        </Vertical>
                      )}
                    </ActionsBar>
                    {!isLaptopAndAbove && !!useEssenceCompetitionStrip && (
                      <EssenceRarityPills
                        rarity={rarity}
                        cardShardsCount={cardShardsChest.cardShardsCount}
                      />
                    )}
                  </Vertical>
                </animated.div>
              </ConditionalWrapper>
            )}
          </BezelContent>
        </ConditionalWrapper>
      </Wrapper>
    </animated.div>
  );
};

CardsPull.fragments = {
  cardPullConfig: gql`
    fragment CardsPull_cardPullConfig on CardPullConfig {
      ...CraftCluesBalance_cardPullConfig
    }
    ${CraftCluesBalance.fragments.cardPullConfig}
  ` as TypedDocumentNode<CardsPull_cardPullConfig>,
  cardShardsChest: gql`
    fragment CardsPull_cardShardsChest on CardShardsChest {
      id
      rarity
      thresholds {
        threshold
      }
      ...RaritySwitcher_cardsShardChest
    }
    ${RaritySwitcher.fragments.cardsShardChest}
  ` as TypedDocumentNode<CardsPull_cardShardsChest>,
};
